<template>
  <!-- New session (NewSession board; the app's new_session_sheet.dart).
       Only "+" (and a drop, Import audio…, Add to session) opens it (C13).
       A sheet from the top of a phone; a 600 px centred sheet from 700 px.
       Name (blank: the date and time), who can join (Invite only, Link,
       Public), Invite people, staged files, Start session. No First loop,
       no Count-in and no mode switch: the web doesn't record, and every
       session loops (SI-26). Mounted once, in App.vue. -->
  <v-dialog
    v-model="dialogVisible"
    class="ns-overlay"
    :class="{ 'ns-overlay--wide': wide }"
    content-class="ns-content"
    :persistent="importing || starting"
    transition="dialog-bottom-transition"
    aria-label="New session"
  >
    <div
      class="ns"
      :class="{ 'is-dropping': dropping }"
      role="dialog"
      aria-label="New session"
      @dragover="onDragOver"
      @dragleave="onDragLeave"
      @drop="onDrop"
    >
      <header class="ns-head">
        <button type="button" class="ns-cancel" :disabled="importing || starting" @click="close">Cancel</button>
        <h2 class="ns-title">New Session</h2>
        <span class="ns-head-spacer" />
      </header>

      <div class="ns-body">
        <label class="ns-name">
          <span class="ns-name-label">Name</span>
          <input
            ref="name"
            v-model="sessionName"
            class="ns-name-input"
            type="text"
            maxlength="100"
            :placeholder="fallbackName"
            autocomplete="off"
            enterkeyhint="go"
            @input="onNameInput"
            @keydown.enter.prevent="start"
          />
        </label>
        <div class="ns-foot" :class="{ 'is-error': !!nameError }">{{ nameError || "Leave it blank and we'll name it after the date and time." }}</div>

        <div class="ns-label">Who can join</div>
        <div class="ns-seg" role="radiogroup" aria-label="Who can join">
          <button
            v-for="a in ACCESS"
            :key="a.value"
            type="button"
            role="radio"
            class="ns-seg-item"
            :class="{ 'is-on': access === a.value }"
            :aria-checked="access === a.value ? 'true' : 'false'"
            @click="access = a.value"
          >
            {{ a.label }}
          </button>
        </div>
        <div class="ns-foot">{{ accessFooter }}</div>

        <button type="button" class="ns-row" @click="inviteAfter = !inviteAfter">
          <ClIcon :path="PERSON_PLUS" :size="20" color="var(--cl-accent-text)" />
          <span class="ns-row-title">Invite people</span>
          <span class="ns-row-value">{{ inviteValue }}</span>
          <svg width="8" height="14" viewBox="0 0 8 14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="ns-chevron" aria-hidden="true"><path d="M1.5 1.5 6.5 7l-5 5.5" /></svg>
        </button>

        <div class="ns-label ns-label--files">
          <span>{{ files.length || importing ? `Files · ${files.length + (importing ? 1 : 0)}` : "Files" }}</span>
          <button v-if="files.length && !importing" type="button" class="ns-link" @click="clearFiles">Clear</button>
        </div>
        <div class="ns-files">
          <div v-if="importing" class="ns-file">
            <ClIcon :path="ARCHIVE" :size="18" color="var(--cl-accent-text)" />
            <span class="ns-file-text">
              <span class="ns-file-name">{{ importFileName }}</span>
              <span class="ns-file-sub">{{ importStatusMessage }}</span>
            </span>
            <span class="ns-progress"><span class="ns-progress-fill" :style="{ width: `${importStatus === 'processing' ? 100 : importProgress}%` }" /></span>
          </div>
          <div v-for="f in files" :key="`${f.name}-${f.size}`" class="ns-file">
            <ClIcon :path="WAVE" :size="18" color="var(--cl-accent-text)" />
            <span class="ns-file-text">
              <span class="ns-file-name">{{ f.name }}</span>
              <span class="ns-file-sub">{{ formatBytes(f.size) }}</span>
            </span>
            <button type="button" class="ns-file-remove" :aria-label="`Remove ${f.name}`" @click="removeFile(f)">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" /></svg>
            </button>
          </div>
          <button type="button" class="ns-file ns-file--add" :disabled="importing" @click="$refs.picker.click()">
            <ClIcon path="M12 5v14M5 12h14" :size="18" :stroke="2" color="var(--cl-accent-text)" />
            <span class="ns-file-text">
              <span class="ns-file-name">Add audio or a project</span>
              <span class="ns-file-sub">WAV, AIFF, FLAC, or an Ableton project</span>
            </span>
          </button>
          <input ref="picker" type="file" :accept="ACCEPT" multiple class="ns-picker" @change="onPick" />
        </div>
        <div v-if="importError" class="ns-foot is-error">{{ importError }}</div>
      </div>

      <div class="ns-start-wrap">
        <button type="button" class="ns-start" :disabled="!canStart" @click="start">
          <v-progress-circular v-if="starting" indeterminate size="18" width="2" color="var(--cl-on-fill)" />
          <span v-else>Start session</span>
        </button>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import bytes from "bytes";
import { v4 as uuidv4 } from "uuid";
import ClIcon from "@/components/kit/ClIcon.vue";
import cloudloop from "@/mixins/cloudloop";
import processFiles from "@/mixins/processFiles";
import sessionLists from "@/stores/sessionLists.js";
import { sessionSheets } from "@/shell/sessionSheets.js";
import { viewport } from "@/shell/sizeClass.js";
import { toast } from "@/shell/toast.js";
import { createSession, inviteToSession, joinSession, sessionExists } from "@/services/api/feed.js";
import { personLabel } from "@/kit/sessionRow.js";
import { ACCEPT, carriesFiles, isArchive, isAudio, isOwnDrag } from "@/components/sessions/dropFiles.js";
import {
  ACCESS,
  accessFooter,
  createSessionBody,
  defaultSessionName,
  nameFromFile,
  rowFromCreated,
  sessionNames,
} from "@/components/sessions/newSession.js";

const PERSON_PLUS = "M6.5 8a3.5 3.5 0 1 0 7 0a3.5 3.5 0 1 0 -7 0M3.5 20c.8-3.6 3.4-5.5 6.5-5.5s5.7 1.9 6.5 5.5M19 8v6M16 11h6";
const WAVE = "M3 12h2M7 8v8M11 5v14M15 9v6M19 7v10";
const ARCHIVE = "M4 7h16v12a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7ZM3 4h18v3H3zM10 11h4";

export default {
  name: "NewSessionModal",
  components: { ClIcon },
  mixins: [cloudloop, processFiles],
  data: () => ({
    ACCESS,
    ACCEPT,
    PERSON_PLUS,
    WAVE,
    ARCHIVE,
    sessionName: "",
    access: "invite",
    inviteAfter: false,
    nameError: "",
    checking: false,
    starting: false,
    files: [],
    dropping: false,
    importing: false,
    importError: "",
    importProgress: 0,
    importStatus: "idle",
    importFileName: "",
    fallbackName: defaultSessionName(),
  }),
  computed: {
    dialogVisible: {
      get() {
        return this.$store.state.showDialog;
      },
      set(value) {
        this.$store.dispatch("setShowDialog", value);
      },
    },
    wide() {
      return viewport.width >= 700;
    },
    me() {
      return this.$store.state.loggedInUser?.username || null;
    },
    invitees() {
      return this.$store.state.newSessionInvitees || [];
    },
    inviteValue() {
      if (this.invitees.length) {
        const names = this.invitees.map((u) => personLabel(null, u));
        return names.length > 2 ? `${names.slice(0, 2).join(", ")} +${names.length - 2}` : names.join(", ");
      }
      return this.inviteAfter ? "After you start" : "Optional";
    },
    accessFooter() {
      return accessFooter(this.access);
    },
    canStart() {
      return !this.starting && !this.importing && !this.nameError && !this.checking;
    },
    importStatusMessage() {
      if (this.importStatus === "processing") return "Unpacking the project…";
      if (this.importStatus === "complete") return "Imported. Opening the session…";
      return `Uploading… ${Math.round(this.importProgress || 0)}%`;
    },
  },
  watch: {
    dialogVisible(open) {
      if (open) {
        this.fallbackName = defaultSessionName();
        this.takeStaged();
        this.$nextTick(() => {
          if (this.wide && this.$refs.name) this.$refs.name.focus();
        });
      } else {
        this.reset();
      }
    },
    "$store.state.stagedNewSessionArchive"(archive) {
      if (archive && this.dialogVisible) this.takeStaged();
    },
    "$store.state.stagedNewSessionAudio": {
      handler(files) {
        if (files && files.length && this.dialogVisible) this.takeStaged();
      },
      deep: true,
    },
  },
  methods: {
    formatBytes(b) {
      if (!b || isNaN(b)) return "0 B";
      return bytes(b, { unitSeparator: " " });
    },
    close() {
      if (this.importing || this.starting) return;
      this.dialogVisible = false;
    },
    reset() {
      if (this.importing) return;
      clearTimeout(this.checkTimer);
      this.sessionName = "";
      this.access = "invite";
      this.inviteAfter = false;
      this.nameError = "";
      this.checking = false;
      this.starting = false;
      this.files = [];
      this.importError = "";
      this.importProgress = 0;
      this.importStatus = "idle";
      this.dropping = false;
      this.$store.commit("setNewSessionInvitees", []);
    },
    // What a drop, Import audio… or Add to session staged.
    takeStaged() {
      const archive = this.$store.state.stagedNewSessionArchive;
      if (archive) {
        this.$store.commit("clearStagedNewSessionArchive");
        this.doImport(archive);
        return;
      }
      const audio = this.$store.state.stagedNewSessionAudio;
      if (audio && audio.length) {
        this.$store.commit("clearStagedNewSessionAudio");
        this.addFiles(audio);
      }
    },
    // Staged audio names a blank session after its first file (SI-25,
    // as today).
    addFiles(list) {
      const valid = Array.from(list || []).filter(isAudio);
      if (!valid.length) return;
      const seen = new Set(this.files.map((f) => `${f.name}:${f.size}`));
      this.files = [...this.files, ...valid.filter((f) => !seen.has(`${f.name}:${f.size}`))];
      if (!this.sessionName.trim()) {
        this.sessionName = nameFromFile(valid[0].name);
        this.onNameInput();
      }
    },
    removeFile(f) {
      this.files = this.files.filter((x) => x !== f);
    },
    clearFiles() {
      this.files = [];
    },
    handleFiles(list) {
      const files = Array.from(list || []);
      const archive = files.find(isArchive);
      if (archive) this.doImport(archive);
      else this.addFiles(files);
    },
    onPick(e) {
      this.handleFiles(e.target.files);
      e.target.value = "";
    },
    onDragOver(e) {
      if (isOwnDrag(e.dataTransfer) || !carriesFiles(e.dataTransfer)) return;
      e.preventDefault();
      this.dropping = true;
    },
    onDragLeave(e) {
      if (e.currentTarget && e.relatedTarget && e.currentTarget.contains(e.relatedTarget)) return;
      this.dropping = false;
    },
    onDrop(e) {
      this.dropping = false;
      if (isOwnDrag(e.dataTransfer)) return;
      e.preventDefault();
      e.stopPropagation();
      this.handleFiles(e.dataTransfer && e.dataTransfer.files);
    },
    // A blank name is the date and time: nothing to check. A taken one:
    // GET /session answers (a 404 means free).
    onNameInput() {
      clearTimeout(this.checkTimer);
      this.nameError = "";
      const name = this.sessionName.trim();
      if (!name) {
        this.checking = false;
        return;
      }
      this.checking = true;
      this.checkTimer = setTimeout(async () => {
        try {
          const taken = await sessionExists(name);
          if (this.sessionName.trim() === name) this.nameError = taken ? "That name is taken. Try another." : "";
        } catch (e) {
          /* the create call checks again */
        } finally {
          if (this.sessionName.trim() === name) this.checking = false;
        }
      }, 400);
    },
    async start() {
      if (!this.canStart) return;
      // A blank name: a key no one can guess, shown by its date and time
      // (S5).
      const { key: name, altName } = sessionNames(this.sessionName);
      const body = createSessionBody(name, this.access, { altName });
      const files = [...this.files];
      const invitees = [...this.invitees];
      const inviteAfter = this.inviteAfter;
      this.starting = true;
      let doc;
      try {
        doc = await createSession(body);
      } catch (e) {
        this.starting = false;
        this.nameError = e && e.status === 403 ? "That name is taken. Try another." : "Couldn’t start the session. Try again.";
        return;
      }
      // Uploads run from here (this dialog stays mounted), into the new
      // session.
      this.sessionName = name;
      if (files.length) {
        this.processFiles(files).catch((err) => toast(typeof err === "string" ? err : "Couldn’t upload those files."));
      }
      try {
        await joinSession(name, this.me);
      } catch (e) {
        /* the creator is already in it */
      }
      for (const u of invitees) {
        inviteToSession(name, u).catch(() => toast(`Couldn’t invite ${personLabel(null, u)}.`));
      }
      sessionLists.upsert(rowFromCreated(doc, body, this.me), "mine");
      this.starting = false;
      this.files = [];
      this.dialogVisible = false;
      await this.$router.push(`/session/${encodeURIComponent(name)}`).catch(() => {});
      if (inviteAfter) sessionSheets.open("invite", { name });
    },
    // An Ableton project or ZIP: imported at once, as today (POST
    // /upload/ableton names the session), then opened.
    doImport(file) {
      if (this.importing) return;
      this.importError = "";
      this.importing = true;
      this.importFileName = file.name;
      this.importProgress = 0;
      this.importStatus = "uploading";
      const uuid = uuidv4();
      const entry = {
        file,
        uuid,
        name: file.name,
        size: file.size,
        sessionName: "New Session",
        status: "uploading",
        fileUrl: "",
        progress: 0.0,
        isArchive: true,
      };
      this.$store.commit("addFile", entry);
      this.importAbletonArchive(file, (p) => {
        this.importProgress = p.percent;
        this.importStatus = p.status;
        this.$store.commit("updateFileProgress", { uuid, progress: p.percent });
        if (p.status === "processing") this.$store.commit("updateFileStatus", { uuid, status: "processing" });
      })
        .then((res) => {
          this.importStatus = "complete";
          this.$store.commit("updateFileStatus", { uuid, status: "complete" });
          setTimeout(() => {
            this.importing = false;
            this.dialogVisible = false;
            const n = res && res.session_name;
            if (n) this.$router.push(`/session/${encodeURIComponent(n)}`);
          }, 350);
        })
        .catch((err) => {
          this.importing = false;
          this.importStatus = "error";
          const text =
            typeof err === "string"
              ? err
              : (err && err.message) || "Import failed. Use an Ableton project zip, .als or .alp.";
          this.importError = text;
          this.$store.commit("updateFile", { uuid, newFile: { ...entry, status: "error", error: text } });
        });
    },
  },
};
</script>

<style>
/* Teleported: not scoped. The page shows through dimmed, as the board's
   pushed-back Sessions. */
.ns-overlay > .v-overlay__scrim {
  background: var(--cl-scrim) !important;
  opacity: 1 !important;
}
.ns-overlay > .v-overlay__content.ns-content {
  position: fixed !important;
  top: 64px !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  margin: 0 !important;
  width: 100% !important;
  max-width: none !important;
  max-height: none !important;
}
.ns-overlay--wide > .v-overlay__content.ns-content {
  top: 0 !important;
  margin: auto !important;
  width: 600px !important;
  height: fit-content !important;
  max-height: calc(100dvh - 48px) !important;
}
</style>

<style scoped>
.ns {
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  border-radius: var(--cl-r-sheet) var(--cl-r-sheet) 0 0;
  background: var(--cl-sheet);
  color: var(--cl-label);
  font-family: var(--cl-font);
  overflow: hidden;
}
.ns-overlay--wide .ns {
  height: auto;
  max-height: calc(100dvh - 48px);
  border-radius: var(--cl-r-2xl);
  border: 1px solid var(--cl-separator);
  box-shadow: 0 24px 60px var(--cl-scrim);
}
.ns.is-dropping {
  outline: 2px dashed var(--cl-accent);
  outline-offset: -6px;
}
.ns-head {
  height: 56px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 8px;
}
.ns-cancel {
  height: 44px;
  min-width: 66px;
  padding: 0 8px;
  border: none;
  background: none;
  color: var(--cl-accent-text);
  font: 400 17px/22px var(--cl-font);
  text-align: left;
  cursor: pointer;
}
.ns-title {
  margin: 0;
  font: 600 17px/22px var(--cl-font);
}
.ns-head-spacer {
  width: 66px;
}
.ns-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 6px 16px 0;
  display: flex;
  flex-direction: column;
}
.ns-name {
  height: 52px;
  flex-shrink: 0;
  border-radius: 12px;
  background: var(--cl-cell-inner);
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 14px;
  cursor: text;
}
.ns-name-label {
  width: 64px;
  font: 400 17px/22px var(--cl-font);
}
.ns-name-input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  color: var(--cl-label);
  font: 400 17px/22px var(--cl-font);
  padding: 0;
}
.ns-name-input::placeholder {
  color: var(--cl-label-3);
}
.ns-foot {
  margin-top: 7px;
  padding: 0 4px;
  font: 400 13px/1.2 var(--cl-font);
  letter-spacing: -0.08px;
  color: var(--cl-label-3);
}
.ns-foot.is-error {
  color: var(--cl-destructive-text);
}
.ns-label {
  margin-top: 24px;
  padding-left: 4px;
  font: 400 13px/1.2 var(--cl-font);
  color: var(--cl-label-2);
}
.ns-label--files {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-right: 4px;
}
.ns-link {
  border: none;
  background: none;
  padding: 0;
  color: var(--cl-accent-text);
  font: 400 13px/1.2 var(--cl-font);
  cursor: pointer;
}
.ns-seg {
  margin-top: 8px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 2px;
  padding: 2px;
  height: 40px;
  flex-shrink: 0;
  box-sizing: border-box;
  border-radius: 10px;
  background: var(--cl-control-fill);
}
.ns-seg-item {
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--cl-label);
  font: 400 14px/1 var(--cl-font);
  cursor: pointer;
}
.ns-seg-item.is-on {
  background: var(--cl-segment-thumb);
  color: var(--cl-on-fill);
  font-weight: 600;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
}
.ns-row {
  margin-top: 10px;
  height: 50px;
  flex-shrink: 0;
  border-radius: 12px;
  border: none;
  background: var(--cl-cell-inner);
  color: var(--cl-label);
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 14px;
  font: 400 17px/22px var(--cl-font);
  cursor: pointer;
  text-align: left;
}
.ns-row-title {
  flex: 1;
}
.ns-row-value {
  font: 400 15px/20px var(--cl-font);
  color: var(--cl-label-50);
}
.ns-chevron {
  color: var(--cl-label-35);
  flex-shrink: 0;
}
.ns-files {
  margin-top: 8px;
  border-radius: 12px;
  background: var(--cl-cell-inner);
  overflow: hidden;
  flex-shrink: 0;
}
.ns-file {
  width: 100%;
  min-height: 50px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 14px;
  box-sizing: border-box;
  border: none;
  border-bottom: 1px solid var(--cl-separator);
  background: transparent;
  color: var(--cl-label);
  text-align: left;
}
.ns-file:last-of-type {
  border-bottom: none;
}
.ns-file--add {
  cursor: pointer;
}
.ns-file--add:disabled {
  opacity: 0.5;
  cursor: default;
}
.ns-file-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.ns-file-name {
  font: 400 15px/20px var(--cl-font);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ns-file-sub {
  font: 400 12px/16px var(--cl-font);
  color: var(--cl-label-3);
}
.ns-file-remove {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: var(--cl-control-fill);
  color: var(--cl-label-2);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
}
.ns-progress {
  width: 64px;
  height: 4px;
  border-radius: 2px;
  background: var(--cl-control-fill);
  overflow: hidden;
  flex-shrink: 0;
}
.ns-progress-fill {
  display: block;
  height: 100%;
  background: var(--cl-accent);
}
.ns-picker {
  display: none;
}
.ns-start-wrap {
  flex-shrink: 0;
  padding: 16px 16px 42px;
}
.ns-overlay--wide .ns-start-wrap {
  padding: 20px 16px 16px;
}
.ns-start {
  width: 100%;
  height: 52px;
  border: none;
  border-radius: 14px;
  background: var(--cl-accent);
  color: var(--cl-on-fill);
  font: 600 17px/1 var(--cl-font);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.ns-start:disabled {
  opacity: 0.55;
  cursor: default;
}
</style>
